import React, { useEffect, useState } from "react";
import { X, Film, Lock } from "lucide-react";
import { MEMORIES, MEMORY_KEYS } from "../../game/memories";

// Panel shows the 3 memory tiles. Clicking an unlocked one plays it inline.
export const MemoriesPanel = ({ open, onClose, flags, onPlay }) => {
  const [playing, setPlaying] = useState(null); // memory key
  const [beat, setBeat] = useState(0);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (e.key === "Escape") {
        if (playing) { setPlaying(null); setBeat(0); }
        else onClose();
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose, playing]);

  if (!open) return null;

  const startMemory = (key) => { setPlaying(key); setBeat(0); };

  const memory = playing ? MEMORIES[playing] : null;
  const currentBeat = memory?.beats?.[beat];
  const isLastBeat = memory && beat === memory.beats.length - 1;

  const advance = () => {
    if (!memory) return;
    if (isLastBeat) {
      // completion
      onPlay(playing, memory);
      setPlaying(null);
      setBeat(0);
    } else {
      setBeat((b) => b + 1);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="memories-modal">
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={() => !playing && onClose()} />

      {/* Grid of tiles */}
      {!playing && (
        <div className="relative w-full max-w-3xl bg-deepshadow border border-white/10 p-10 mx-4 animate-fadein">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <Film size={16} className="text-white/60" />
              <h3 className="font-mono text-xs tracking-[0.35em] uppercase text-white/60">
                MEMORIES // {MEMORY_KEYS.filter((k) => flags[k]).length} / {MEMORY_KEYS.length} UNLOCKED
              </h3>
            </div>
            <button onClick={onClose} className="text-white/50 hover:text-emergency transition-colors" data-testid="memories-close">
              <X size={18} />
            </button>
          </div>
          <p className="narrative text-white/50 text-sm mb-8 italic max-w-lg">
            Some things Jack carries are not items. Hold a keepsake long enough and the past holds you back.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {MEMORY_KEYS.map((k, i) => {
              const m = MEMORIES[k];
              const unlocked = !!flags[k];
              const played = !!flags[`played_${k}`];
              return (
                <button
                  key={k}
                  onClick={() => unlocked && startMemory(k)}
                  disabled={!unlocked}
                  data-testid={`memory-tile-${i}`}
                  className={`text-left border p-5 transition-colors group ${
                    unlocked
                      ? "border-white/15 hover:border-emergency/70 bg-black/40 cursor-pointer"
                      : "border-white/5 bg-black/60 cursor-not-allowed"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">
                      MEM. {String(i + 1).padStart(2, "0")}
                    </span>
                    {!unlocked && <Lock size={12} className="text-white/30" />}
                    {played && <span className="font-mono text-[10px] text-emergency/70">● SEEN</span>}
                  </div>
                  {unlocked ? (
                    <>
                      <p className="font-serif italic text-lg text-white/90 group-hover:text-white leading-tight">{m.title}</p>
                      <p className="narrative text-xs text-white/40 mt-2">{m.subtitle}</p>
                    </>
                  ) : (
                    <>
                      <p className="font-serif italic text-lg text-white/20 leading-tight">— locked —</p>
                      <p className="narrative text-xs text-white/25 mt-2">{m.hint}</p>
                    </>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Playback view */}
      {playing && memory && (
        <div className="relative w-full max-w-2xl px-8 md:px-12 py-16 animate-fadein" data-testid="memory-playback">
          <div className="mb-8">
            <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-emergency/70 mb-3">
              MEMORY // {memory.title.toUpperCase()}
            </p>
            <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-white/40">
              BEAT {beat + 1} / {memory.beats.length}
            </p>
          </div>

          <div
            key={`${playing}-${beat}`}
            className={`narrative text-xl md:text-2xl leading-relaxed max-w-xl animate-fadein ${
              currentBeat?.kind === "wrong" ? "text-emergency/85 whisper" : "text-white/90"
            }`}
            data-testid="memory-beat"
          >
            <p>{currentBeat?.text}</p>
            {currentBeat?.subline && (
              <p className="mt-4 text-sm md:text-base text-white/50 italic">{currentBeat.subline}</p>
            )}
          </div>

          <div className="mt-14 flex items-center gap-4">
            <button
              onClick={advance}
              className={`term-btn ${currentBeat?.kind === "wrong" ? "term-btn-danger" : ""}`}
              data-testid="memory-continue"
            >
              {isLastBeat ? "LET THE MEMORY GO" : "CONTINUE"}
            </button>
            <button
              onClick={() => { setPlaying(null); setBeat(0); }}
              className="text-white/40 hover:text-white/70 text-xs font-mono tracking-widest transition-colors"
              data-testid="memory-abandon"
            >
              // stop remembering
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
